import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Users, Lock, Shield, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaasAdminOverview({ admin }) {
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('saas_admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, orgsRes] = await Promise.all([
        axios.get(`${API}/saas-admin/stats`, { headers }),
        axios.get(`${API}/saas-admin/organizations`, { headers })
      ]);
      setStats(statsRes.data);
      setOrgs(orgsRes.data);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">Loading overview...</p>
      </div>
    );
  }

  const statCards = [
    { label: 'Organizations', value: stats?.total_organizations || 0, icon: Building2, color: 'text-blue-400 bg-blue-400/10' },
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-emerald-400 bg-emerald-400/10' },
    { label: 'Credentials', value: stats?.total_credentials || 0, icon: Lock, color: 'text-purple-400 bg-purple-400/10' },
    { label: 'Certificates', value: stats?.total_certificates || 0, icon: Shield, color: 'text-amber-400 bg-amber-400/10' },
  ];

  const planCounts = {
    starter: orgs.filter(o => (o.subscription_plan || 'starter') === 'starter').length,
    professional: orgs.filter(o => o.subscription_plan === 'professional').length,
    enterprise: orgs.filter(o => o.subscription_plan === 'enterprise').length,
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-manrope font-bold text-3xl text-white tracking-tight mb-1">
          Admin Overview
        </h1>
        <p className="text-gray-400 text-sm">Welcome back, {admin?.full_name}</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card) => (
          <div key={card.label} className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid={`admin-stat-${card.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className={`inline-flex p-2.5 rounded-lg mb-3 ${card.color}`}>
              <card.icon size={20} />
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{card.label}</p>
            <p className="font-manrope font-bold text-2xl text-white">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Subscription Distribution */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-amber-500" />
          <h2 className="font-manrope font-semibold text-lg text-white">Subscription Plans</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">Starter (Free)</p>
            <p className="font-bold text-2xl text-white">{planCounts.starter}</p>
            <p className="text-xs text-gray-500 mt-1">organizations</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-amber-500/30">
            <p className="text-amber-400 text-sm mb-1">Professional ($29/mo)</p>
            <p className="font-bold text-2xl text-white">{planCounts.professional}</p>
            <p className="text-xs text-gray-500 mt-1">organizations</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-purple-500/30">
            <p className="text-purple-400 text-sm mb-1">Enterprise (Custom)</p>
            <p className="font-bold text-2xl text-white">{planCounts.enterprise}</p>
            <p className="text-xs text-gray-500 mt-1">organizations</p>
          </div>
        </div>
      </div>

      {/* Recent Orgs */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <h2 className="font-manrope font-semibold text-lg text-white mb-4">Recent Organizations</h2>
        <div className="space-y-3">
          {orgs.slice(0, 5).map((org) => (
            <div key={org.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 font-bold text-sm">
                  {org.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{org.name}</p>
                  <p className="text-gray-500 text-xs">{org.user_count} users &middot; {org.credential_count} creds &middot; {org.certificate_count} certs</p>
                </div>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                org.subscription_plan === 'enterprise' ? 'bg-purple-500/10 text-purple-400' :
                org.subscription_plan === 'professional' ? 'bg-amber-500/10 text-amber-400' :
                'bg-gray-700 text-gray-400'
              }`}>
                {(org.subscription_plan || 'starter').charAt(0).toUpperCase() + (org.subscription_plan || 'starter').slice(1)}
              </span>
            </div>
          ))}
          {orgs.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No organizations yet</p>}
        </div>
      </div>
    </div>
  );
}
